<template>
  <div class="confirm-sign-page">
    <h1>确认参会并签名</h1>
    <!-- 参会人ID信息已删除 -->
    <div class="signature-area" v-if="urlValid">
      <canvas ref="signatureCanvas" width="400" height="200"
              @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing" @mouseleave="stopDrawing"
              style="border:1px solid #ccc; background:#fff;"></canvas>
      <div class="signature-actions">
        <button @click="clearSignature">重新签名</button>
        <button @click="submitSignature" :disabled="!hasSignature">确认参会并签名</button>
      </div>
    </div>
    <div class="error-message" v-else>
      <p>URL无效或已过期，无法进行签名操作</p>
      <p>请联系管理员获取新的签名链接</p>
    </div>
    <div class="status-message" v-if="statusMessage">
      {{ statusMessage }}
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
export default {
  name: 'ConfirmAttendanceSign',
  data() {
    return {
      participantId: '', // 参会人ID
      timestamp: '', // 时间戳
      signature: '', // 签名
      hasSignature: false, // 是否已签名
      drawing: false, // 是否正在绘制
      lastX: 0, // 上一次X坐标
      lastY: 0, // 上一次Y坐标
      statusMessage: '', // 状态提示
      urlValid: false // URL是否有效
    }
  },
  mounted() {
    this.parseUrlParams(); // 解析URL参数
    // 等待 DOM 更新后初始化画布
    this.$nextTick(() => {
      if (this.urlValid) {
        this.initCanvas();
      }
    });
  },
  methods: {
    // 解析URL参数并验证签名
    parseUrlParams() {
      // 从路由参数中获取
      this.participantId = this.$route.query.participantId || '';
      this.timestamp = this.$route.query.timestamp || '';
      console.log('解析到的参数:', {
        participantId: this.participantId,
        timestamp: this.timestamp
      });
      
      // 验证参数是否完整
      if (this.participantId && this.timestamp) {
        this.urlValid = this.verifySignatureUrl();
        if (this.urlValid) {
          this.statusMessage = '请进行签名';
        } else {
          this.statusMessage = 'URL已过期，请联系管理员';
        }
      } else {
        this.urlValid = false;
        this.statusMessage = 'URL参数不完整，请联系管理员';
      }
    },
    
    // 前端验证URL是否有效
    verifySignatureUrl() {
      try {
        // 1. 检查参与人ID是否存在
        if (!this.participantId) {
          console.error('参与人ID不存在');
          return false;
        }

        // 2. 检查时间戳是否存在且有效
        if (!this.timestamp) {
          console.error('时间戳不存在');
          return false;
        }

        // 3. 检查是否过期（时间戳应该大于当前时间）
        const currentTime = Date.now();
        const expireTime = parseInt(this.timestamp);
        console.log('时间验证:', {
          currentTime,
          expireTime,
          diff: expireTime - currentTime
        });

        if (currentTime > expireTime) {
          console.error('URL已过期');
          return false;
        }

        return true;
      } catch (error) {
        console.error('验证出错:', error);
        return false;
      }
    },
    
    // 生成签名（与后端逻辑保持一致）
    generateSignature() {
      try {
        // 构造签名字符串：参与人ID + 时间戳 + 密钥
        const secretKey = 'zc_writ_signature_secret_2024'; // 需要与后端保持一致
        const signString = this.participantId + '|' + this.timestamp + '|' + secretKey;
        
        // 使用简单的哈希算法模拟MD5（前端简化版本）
        // 注意：这里使用简化算法，实际项目中建议使用crypto-js等库
        let hash = 0;
        for (let i = 0; i < signString.length; i++) {
          const char = signString.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // 转换为32位整数
        }
        
        // 转换为字符串并Base64编码
        const hashStr = Math.abs(hash).toString();
        return btoa(hashStr);
        
      } catch (error) {
        return '';
      }
    },
    // 初始化签名画布
    initCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#222';
        ctx.lineCap = 'round';
      }
    },
    // 开始绘制
    startDrawing(e) {
      this.drawing = true;
      const { x, y } = this.getCanvasPos(e);
      this.lastX = x;
      this.lastY = y;
    },
    // 绘制过程
    draw(e) {
      if (!this.drawing) return;
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      const { x, y } = this.getCanvasPos(e);
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      this.lastX = x;
      this.lastY = y;
      this.hasSignature = true;
    },
    // 结束绘制
    stopDrawing() {
      this.drawing = false;
    },
    // 获取鼠标在canvas上的坐标
    getCanvasPos(e) {
      const rect = this.$refs.signatureCanvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      return { x, y };
    },
    // 清除签名
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.hasSignature = false;
      this.statusMessage = '';
    },
    // 提交签名
    async submitSignature() {
      if (!this.urlValid) {
        this.statusMessage = 'URL无效，无法提交签名';
        return;
      }
      
      if (!this.hasSignature) {
        this.statusMessage = '请先签名';
        return;
      }
      
      const canvas = this.$refs.signatureCanvas;
      const base64Image = canvas.toDataURL('image/png');
      try {
        // 调用后端接口提交签名
        console.log('准备提交签名:', {
          participantId: this.participantId,
          imageLength: base64Image.length
        });
        const response = await request.post('/api/case-hearing-participant/sign', {
          participantId: this.participantId,
          signImageBase64: base64Image
        }, {
          showLoading: true
        });
        console.log('提交签名响应:', response);
        if (response.code === 200) {
          this.statusMessage = '签名成功！您已确认参会。';
        } else {
          this.statusMessage = response.message || '签名失败，请重试。';
        }
      } catch (error) {
        console.error('签名提交失败:', error);
        this.statusMessage = error.message || '签名提交失败，请检查网络或稍后重试。';
      }
    }
  }
}
</script>

<style scoped>
.confirm-sign-page {
  max-width: 500px;
  margin: 40px auto;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.participant-info {
  margin-bottom: 16px;
}
.signature-area {
  margin: 24px 0;
  text-align: center;
}
.signature-actions {
  margin-top: 12px;
}
.status-message {
  margin-top: 20px;
  color: #1890ff;
  font-weight: bold;
}
.error-message {
  margin-top: 20px;
  color: #ff4d4f;
  text-align: center;
  padding: 20px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
}
</style> 